// How long the review screen waits for one engine answer before it stops waiting (2026-10-10). A save that
// never got an answer left Confirm and next spinning for good on prod: the browser's fetch has no time limit,
// and a request lost between a restarting engine host, its channel and the browser is never answered. A run
// (a reading, a re-check of types, a batch read again) answers its ticket at once and is followed by status
// asks, so every answer here is short; a person's single-document write and its read-back are shorter still.
import { EngineUnreachableError } from './engine-unreachable';

/** A person's single-document writes and the read-back of the document: seconds on the host. */
const PERSONS_WRITES = new Set([
  'save-document-correction',
  'save-document-corrections',
  'undo-field-edit',
  'batch-document',
]);

/** The wait for a person's write or its read-back. */
export const WRITE_LIMIT_MS = 60_000;

/** The wait for any other answer: well past the slowest command answered outright, under the channel's 30 minutes. */
export const ANSWER_LIMIT_MS = 5 * 60_000;

/**
 * A person's re-reads of one page or one document (Turn page reads the page and the document's values again): a
 * minute or two at most when Cortexa is busy. On prod 2026-10-10 a Turn page waited 7 minutes behind a capture
 * stream and its spinner turned all that time; it now goes ahead of captures, and the screen stops waiting at 2 min.
 */
const PERSONS_READS = new Set([
  'turn-page',
  'reextract-prepared-document',
  'reclassify-prepared-document',
  'find-document-table',
]);

/** The wait for a person's re-read of one page or document. */
export const READ_LIMIT_MS = 120_000;

export function answerLimit(command: string): number {
  if (PERSONS_WRITES.has(command)) return WRITE_LIMIT_MS;
  return PERSONS_READS.has(command) ? READ_LIMIT_MS : ANSWER_LIMIT_MS;
}

/** What a person reads when one of their actions got no answer in time: what did not happen, and to try again. */
const NO_ANSWER: Record<string, string> = {
  // The review screen's own words when a turn fails (CapturePoint afd1f3b2): the page turns back on screen.
  'turn-page': "Couldn't turn the page. Try again.",
  'reextract-prepared-document':
    "Couldn't read this document again just now. Nothing changed: try again.",
  'reclassify-prepared-document':
    "Couldn't read this document again just now. Nothing changed: try again.",
  'find-document-table': "Couldn't look for the table just now. Nothing changed: try again.",
};

/** The engine did not answer within its limit: nothing is known to have changed, so asking again is the way on. */
export class EngineAnswerTimeoutError extends EngineUnreachableError {
  constructor(readonly command: string) {
    super(0);
    this.name = 'EngineAnswerTimeoutError';
    if (NO_ANSWER[command]) this.message = NO_ANSWER[command];
  }
}

/** Whether a fetch failed because its time limit passed. */
export function isTimeout(reason: unknown): boolean {
  return (
    reason instanceof DOMException &&
    (reason.name === 'TimeoutError' || reason.name === 'AbortError')
  );
}
